import { useEffect, useRef } from 'react';
import { hasGeneratedMediaQuotaImpact } from '../../../utils/generatedImageMessage';

interface Options<TMessage extends { id?: string; role: string }> {
  enabled: boolean;
  chatStatus: string;
  conversationId: string | null | undefined;
  messages: TMessage[] | undefined;
  isCurrentConversationMessage: (message: TMessage) => boolean;
}

/** Asks the quota card to refetch once per generated file/image message after the stream is done. */
export function useMediaQuotaRefreshOnGeneration<TMessage extends { id?: string; role: string }>({
  enabled,
  chatStatus,
  conversationId,
  messages,
  isCurrentConversationMessage,
}: Options<TMessage>) {
  const lastRefreshKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || chatStatus !== 'ready') return;

    const latest = [...(messages || [])].reverse().find((message) => {
      if (!isCurrentConversationMessage(message) || message.role === 'user') return false;
      const metadata = (message as unknown as { metadata?: unknown }).metadata;
      return hasGeneratedMediaQuotaImpact(metadata);
    });
    if (!latest?.id) return;

    const key = `${conversationId}\u0000${latest.id}`;
    if (lastRefreshKeyRef.current === key) return;
    lastRefreshKeyRef.current = key;
    window.dispatchEvent(new CustomEvent('media-quota:refresh'));
  }, [chatStatus, conversationId, enabled, isCurrentConversationMessage, messages]);
}
